<template>
<m-pageframe
    title="Remove Bulk Message Recipients"
>
<v-container
    fluid
>
    <m-beta-alert code="bulk-recipient-remove-recent-order" />

    <v-card
        elevation="0"
    >
        <v-card-title class="text-h5 font-weight-bold">Settings</v-card-title>

        <v-list>
            <v-list-item>
                <v-list-item-content>
                    <m-edit-text
                        label="Remove all recipients that have placed an order within (days)"
                        :value="past_n_days_description"
                        :custom_on_edit="onEditPastDays"
                    />
                </v-list-item-content>
            </v-list-item>

            <v-list-item>
                <v-list-item-action>
                    <v-checkbox
                        label="Ignore canceled orders (recipients will not be removed)"
                        v-model="ignore_canceled"
                    />
                </v-list-item-action>
            </v-list-item>

            <v-list-item>
                <v-list-item-action>
                    <v-checkbox
                        label="Ignore orders with $0 total value (recipients will not be removed)"
                        v-model="ignore_zero_value"
                    />
                </v-list-item-action>
            </v-list-item>

            <v-list-item>
                <v-list-item-content>
                    <v-select
                        label="Order types to match (optional)"
                        v-model="order_types"
                        :items="available_order_types"
                        multiple
                        chips
                        deletable-chips
                        clearable
                        :disabled="loading_order_types"
                        :loading="loading_order_types"
                        hint="Leave empty to remove recipients with any order type. When set, recipients are removed only if they have an orders that matches one of the selected types."
                        persistent-hint
                    />
                </v-list-item-content>
            </v-list-item>
        </v-list>

        <v-card-actions>
            <v-btn
                v-on:click="onClickReturnToBulkMessage"
            >
                Go back to bulk message
            </v-btn>

            <v-btn
                color="primary"
                v-on:click="onClickRemoveRecipients"
                :disabled="null == this.past_n_days || sync_started"
            >
                Check {{content_type_description}} and Remove Recipients
            </v-btn>
        </v-card-actions>
    </v-card>

    <v-card
        v-if="sync_started"
        elevation="0"
    >
        <v-card-title>Recent Orders</v-card-title>

        <v-card-text>
            <v-alert
                v-if="sync_finished"
                border="left"
                colored-border
                color="primary"
                elevation="2"
                type="success"
            >
                Task finished. {{ recipients_removed_description }} <a @click="onClickReturnToBulkMessage">Go back to bulk message</a>
            </v-alert>
        
            <v-data-table
                :headers="recipient_headers"
                :items="tasks"
                :items-per-page="100"
                class="elevation-1"
                :hide-default-footer="true"
                disable-sort
                :footer-props="{
                    disableItemsPerPage: false,
                    disablePagination: false,
                }"
            >
            
                <template v-slot:item.time_start="{ item }">
                    {{ $rc.pretty(item.time_start, 'date') }}
                </template>
            
                <template v-slot:item.state="{ item }">
                    {{ $rc.enumObject('crm_sync_chunk_state', item.state)?.text }}
                </template>
            
                <template v-slot:item.count_order="{ item }">
                    {{ $rc.pretty(item.count_order, 'int') }}
                </template>
            
                <template v-slot:item.count_recipient_removed="{ item }">
                    {{ $rc.pretty(item.count_recipient_removed, 'int') }}
                </template>
            
            </v-data-table>
        </v-card-text>
    </v-card>

</v-container>
</m-pageframe>
</template>

<script>
export default {
    data() {
        return {
            bulk_message: null,
            
            past_n_days: null,
            ignore_canceled: false,
            ignore_zero_value: false,
            
            order_types: [],
            available_order_types: [],
            loading_order_types: false,
            
            batch_id: null,
            tasks: [],

            interval: null,
        }
    },

    mounted () {
        this.getBulkMessage();
        this.loadAvailableOrderTypes();
    },

    beforeDestroy () 
    {
        this.disableInterval();
    },

    methods: {
        getBulkMessage()
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'GET: /api/v1/bulk/messages/get-profile/:bulk_message_id',
            
                'params' : {
                    'bulk_message_id': this.$route.params.bulk_message_id,
                },
            
                'on_success': (data) => {
                    this.bulk_message = data;
                },
            
                'on_error': 'Unable to get bulk message',
            });
        },

        loadAvailableOrderTypes()
        {
            this.loading_order_types = true;
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/integrations/crm/order-types/:organization_id',
            
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                },
            
                'on_success': (data) => {
                    this.available_order_types = (data.order_types || [])
                        .filter(item => item.content_type === 'commerce7')
                        .map(item => ({ text: item.value_label, value: item.value_id }));
                    this.loading_order_types = false;
                },
            
                'on_error': () => {
                    this.available_order_types = [];
                    this.loading_order_types = false;
                },
            });
        },

        onEditPastDays()
        {
            this.$rc.prompt({
                    'title' : 'Remove recent orders settings',
                    'label' : 'Remove all recipients that have placed an order within (days)',
                    'value' : this.past_n_days,
                    'is_valid': this.$rc.validationRuleInteger(1, null),
                    'after_prompt': (value) => this.past_n_days = value
                });
        },

        onClickRemoveRecipients()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'POST: /api/v1/bulk/recipients/remove-recent-orders',
            
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'bulk_message_id': this.$route.params.bulk_message_id,
                    'past_n_days': this.past_n_days,
                    'ignore_canceled': this.ignore_canceled,
                    'ignore_zero_value': this.ignore_zero_value,
                    'order_types': this.order_types,
                },
            
                'on_success': (data) => {
                    this.batch_id = data.batch_id;
                    this.tasks = data.results;

                    this.interval = window.setInterval(this.listTasks, 3000);
                },
            
                'on_error': 'Unable to remove recipients (unexpected API error)',
            });
        },

        listTasks()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/bulk/recipients/remove-recent-orders/:batch_id',
            
                'params' : {
                    'batch_id': this.batch_id,
                },
            
                'on_success': (data) => {
                    this.tasks = data.results;

                    if (this.sync_finished)
                    {
                        this.disableInterval();
                    }
                },
            
                'on_error': 'Unable to list integration sync tasks',
            });
        },

        disableInterval()
        {
            if ( this.interval != null )
            {
                window.clearInterval(this.interval);
                this.interval = null;
            }
        },

        onClickReturnToBulkMessage()
        {
            this.$router.push({
                'name': 'view-bulk-message',
                'params' : {
                    'organization_id' : this.$route.params.organization_id,
                    'bulk_message_id' : this.$route.params.bulk_message_id,
                }
            }).catch(e=>{}); // swallow any exception
        },

    },

    computed: {
        content_type_description()
        {
            // TODO This should be made dynamic if/when this feature is available to other integrations
            return 'Commerce7';
        },

        sync_started()
        {
            return null != this.batch_id;
        },

        sync_finished()
        {
            if (! this.sync_started) return false;

            return this.tasks.every(task => task.state.startsWith('finished-'));
        },

        recipients_removed()
        {
            if (! this.tasks) return 0;

            return this.tasks
                .map(task => task.count_recipient_removed)
                .reduce((l, r) => l + r, 0);
        },

        recipients_removed_description()
        {
            if (1 == this.recipients_removed)
            {
                this.recipients_removed + ' recipient has been removed.';
            }
            
            return this.recipients_removed + ' recipients have been removed.';
        },

        past_n_days_description()
        {
            if (null == this.past_n_days) return;

            if (1 == this.past_n_days) return "1 day";
            else return this.past_n_days + " days";
        },

        recipient_headers()
        {
            let ret = [];

            ret.push({ text: 'Date', value: 'time_start'});
            ret.push({ text: 'Status', value: 'state'});
            ret.push({ text: 'Orders', value: 'count_order'});
            ret.push({ text: 'Recipients Removed', value: 'count_recipient_removed'});

            return ret;
        },
    },
}
</script>

<style lang="scss" scoped>
</style>
